// AuthContext.js — état utilisateur global (espace membre VertinGo).
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { nakamaAuth } from '../services/nakamaAuth';
import { resolveUser, cachedUser } from '../services/userCache';

const AuthContext = createContext(null);

// DÉ-DUPLICATION des notifications au niveau MODULE (survit aux re-runs de l'effet / remontages) :
// une notif re-livrée (reconnexion socket, natif Nakama + custom au même texte) n'est comptée/affichée
// qu'UNE fois. Sinon un state recréé à chaque re-run laissait passer des doublons.
const _seenNotifIds = new Set();
const _recentSubjects = new Map();

// Toast d'INVITATION À UN GROUPE : Accepter (rejoint le groupe) / Refuser. L'acceptation appelle vg_group_accept.
function GroupInviteToast({ subject, content, closeToast }) {
  const [done, setDone] = useState('');
  const accept = async () => {
    try {
      const r = await nakamaAuth.rpc('vg_group_accept', { group_id: content.group_id });
      let ok = false; try { ok = !!JSON.parse(r || '{}').ok; } catch { ok = false; }
      setDone(ok ? 'joined' : 'error');
      if (ok) { try { window.dispatchEvent(new CustomEvent('vg-groups-reload')); } catch (e) { /* */ } }
    } catch { setDone('error'); }
  };
  if (done === 'joined') return <div>✅ Tu as rejoint « {content.group_name || 'le groupe'} ».</div>;
  if (done === 'error') return <div>❌ Impossible de rejoindre (limite de 5 groupes atteinte ou erreur).</div>;
  return (
    <div>
      <div style={{ marginBottom: 8 }}>{subject}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={(e) => { e.stopPropagation(); accept(); }} style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 12px', fontWeight: 700, cursor: 'pointer' }}>Accepter</button>
        <button onClick={(e) => { e.stopPropagation(); if (closeToast) closeToast(); }} style={{ background: 'transparent', color: '#bbb', border: '1px solid #666', borderRadius: 8, padding: '4px 12px', cursor: 'pointer' }}>Refuser</button>
      </div>
    </div>
  );
}
const NOTIF_SUBJECT_WINDOW = 8000; // même sujet re-reçu dans cette fenêtre → ignoré

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [account, setAccount] = useState(null);
  const [ready, setReady] = useState(false);
  const [notifCount, setNotifCount] = useState(0);

  const refreshAccount = useCallback(async () => {
    const acc = await nakamaAuth.getAccount();
    setAccount(acc);
    return acc;
  }, []);

  useEffect(() => {
    let unsub = nakamaAuth.onChange((s) => setSession(s));
    (async () => {
      await nakamaAuth.restore();
      if (nakamaAuth.isLoggedIn()) await refreshAccount();
      setReady(true);
    })();
    // notifications temps réel → compteur + toast visible (ex: invitation à un salon)
    // DÉ-DUPLICATION (état module _seenNotifIds/_recentSubjects) : la même notif peut arriver 2×
    // (double livraison socket, reconnexion, ou natif Nakama + custom au même texte). On ignore un id
    // déjà vu, et un même sujet reçu dans la fenêtre. UNE notif = UN événement.
    const unsubN = nakamaAuth.onNotification((n) => {
      const id = n && (n.id || n.notification_id);
      let subject = (n && (n.subject || (n.content && n.content.message))) || 'Nouvelle notification';
      const now = Date.now();
      // JAMAIS d'id dégueu dans le toast → on relibelle la demande/acceptation d'ami (natif Nakama, anglais)
      // en FR avec le NOM AppStore de l'expéditeur (repli générique SANS id tant que le nom n'est pas résolu).
      const _uid = n && (n.sender_id || (n.content && (n.content.sender_id || n.content.user_id)));
      const _isFriend = n && (n.code === -2 || /friend request|wants to add you|added you as a friend|be your friend|accepted your/i.test(subject));
      if (_isFriend) {
        const acc = /accept/i.test(subject);
        const nm = _uid && cachedUser(_uid) && cachedUser(_uid).name;
        if (nm) subject = acc ? `👤 ${nm} a accepté ta demande d'ami` : `👤 ${nm} t'a envoyé une demande d'ami`;
        else { subject = acc ? '👤 Demande d\'ami acceptée' : '👤 Nouvelle demande d\'ami'; if (_uid) resolveUser(_uid); }
      } else if (_uid && subject.includes(_uid)) {
        const nm = cachedUser(_uid) && cachedUser(_uid).name;
        if (nm) subject = subject.split(_uid).join(nm); else resolveUser(_uid);
      }
      // NOTIF NATIVE NAKAMA « X wants to chat » = simple adhésion à un canal DM (déclenchée quand un ami se connecte
      // et que son client rejoint le canal), PAS un vrai message → on l'IGNORE totalement (ni toast, ni compteur).
      // Les vrais MP sont notifiés par DMNotifier (« … t'a écrit un message privé »).
      if (/wants to chat/i.test(subject)) return;
      if (id && _seenNotifIds.has(id)) return;
      const last = _recentSubjects.get(subject);
      if (last && now - last < NOTIF_SUBJECT_WINDOW) return;
      if (id) { _seenNotifIds.add(id); if (_seenNotifIds.size > 500) _seenNotifIds.delete(_seenNotifIds.values().next().value); }
      _recentSubjects.set(subject, now);
      setNotifCount((c) => c + 1);
      // INVITATION À UN GROUPE → toast avec Accepter/Refuser (au lieu du toast générique).
      const content = (n && n.content) || {};
      if (content.type === 'group_invite' && content.group_id) {
        try { toast.info(({ closeToast }) => <GroupInviteToast subject={subject} content={content} closeToast={closeToast} />, { autoClose: false, closeOnClick: false }); }
        catch { try { toast.info(subject); } catch (e) { /* */ } }
        return;
      }
      // Toast CLIQUABLE → espace membre, onglet Notifications, sur la notif reçue (même logique que la cloche).
      try {
        toast.info(subject, {
          onClick: () => {
            try { window.dispatchEvent(new CustomEvent('vg-navigate', { detail: '/compte?tab=notifs' })); } catch (e) { /* */ }
            try { setTimeout(() => window.dispatchEvent(new CustomEvent('vg-open-notifs')), 120); } catch (e) { /* */ }
          },
          style: { cursor: 'pointer' },
        });
      } catch { /* */ }
    });
    return () => { unsub(); unsubN(); };
  }, [refreshAccount]);

  // recharge le compte + notifs à chaque changement de session
  useEffect(() => {
    if (session) {
      refreshAccount();
      // Badge = notifs NON LUES (plus récentes que la dernière consultation), pas le total → il se vide
      // quand on consulte l'onglet et RESTE vide au refresh.
      nakamaAuth.unreadNotifCount().then(setNotifCount);
    } else {
      setAccount(null); setNotifCount(0);
    }
  }, [session, refreshAccount]);

  // Statut Premium : lu depuis les métadonnées du compte Nakama (metadata.premium === true).
  // Débloqué plus tard par un paiement ; ici on lit simplement le flag (sans-pub + avantages).
  let isPremium = false;
  try {
    const md = account && account.user && account.user.metadata;
    const meta = typeof md === 'string' ? JSON.parse(md) : (md || {});
    isPremium = meta.premium === true || meta.premium === 'true';
  } catch { isPremium = false; }

  // INVITÉ = session sans identité liée (ni e-mail, ni Google/Facebook, ni custom) OU repli local hors-ligne.
  // Il peut TOUT tester, mais pour prendre Premium il doit créer un vrai compte (email/Google/Facebook).
  // NB : un invité device-auth est un vrai compte Nakama (XP conservée) → lier une identité garde l'XP.
  let isGuest = false;
  try {
    if (session && session._localGuest) isGuest = true;
    else if (session && account) {
      const u = account.user || {};
      const hasIdentity = !!(account.email || account.custom_id || u.google_id || u.facebook_id || u.apple_id);
      isGuest = !hasIdentity;
    }
  } catch { isGuest = false; }

  const value = {
    enabled: nakamaAuth.enabled,
    ready,
    session,
    account,
    user: account ? account.user : null,
    isLoggedIn: !!session,
    isPremium,
    isGuest,
    notifCount,
    setNotifCount,
    refreshAccount,
    auth: nakamaAuth,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans <AuthProvider>');
  return ctx;
}
